import { Permission, UserRole, currentReturnTo } from '@/config/auth0.config'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuth0 } from '@auth0/auth0-react'
import { Alert, Center, Loader, LoadingOverlay, Stack, Text } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import { ReactNode, useEffect } from 'react'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRole?: UserRole
  requiredPermission?: Permission
}

export const ProtectedRoute = ({
  children,
  requiredRole,
  requiredPermission
}: ProtectedRouteProps) => {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
  } = useAuth0()

  const { userRole, hasPermission, isAdmin } = usePermissions()

  // Redirect to Auth0 login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: currentReturnTo() },
        authorizationParams: {
          screen_hint: 'login',
        }
      })
    }
  }, [isAuthenticated, isLoading, loginWithRedirect])

  if (isLoading) {
    return (
      <LoadingOverlay visible>
        <Center h="100vh">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">Authenticating...</Text>
          </Stack>
        </Center>
      </LoadingOverlay>
    )
  }

  if (!isAuthenticated) {
    // This will show briefly before redirect
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    )
  }

  // Admin users have access to everything
  if (isAdmin) {
    return <>{children}</>
  }

  // Check role-based access
  if (requiredRole && userRole !== requiredRole) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md" maw={400}>
          <IconAlertTriangle size={48} color="orange" />
          <Text size="xl" fw={700}>Access Denied</Text>
          <Text size="sm" c="dimmed" ta="center">
            You don't have permission to access this page. Your role: <strong>{userRole}</strong>
          </Text>
          <Alert color="blue" title="Required Role">
            This page requires: <strong>{requiredRole}</strong>
          </Alert>
        </Stack>
      </Center>
    )
  }

  // Check permission-based access
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md" maw={400}>
          <IconAlertTriangle size={48} color="orange" />
          <Text size="xl" fw={700}>Insufficient Permissions</Text>
          <Text size="sm" c="dimmed" ta="center">
            You don't have the required permission to access this feature.
          </Text>
          <Alert color="blue" title="Required Permission">
            <strong>{requiredPermission}</strong>
          </Alert>
        </Stack>
      </Center>
    )
  }

  return <>{children}</>
}
